import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowUpRight, CalendarDays, HandCoins, Headphones, Trophy } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, { PageHero, LightSection, ClosingBanner, PrimaryCTA, SecondaryCTA } from '@/components/PageShell';
import { resourcesArticles as articles } from '@/lib/data/resources';

// The four content hubs (docs/06-Go-to-Market.md, "Content strategy"). They sit above the article
// grid because each answers a "where do I find money / when do I need it"
// question a family brings with them, rather than one we chose to write about.
const TOOLKITS = [
  {
    to: '/resources/fundraising-guides',
    icon: Trophy,
    eyebrow: 'By sport',
    title: 'Fundraising guides by sport',
    body: 'Real costs, the money calendar and ideas that fit travel baseball, club volleyball, club soccer and swimming.',
  },
  {
    to: '/resources/youth-sports-grants',
    icon: HandCoins,
    eyebrow: 'Find money',
    title: 'Youth sports grants directory',
    body: 'Verified programs that help with fees, travel, training and gear — and whether you or your club applies.',
  },
  {
    to: '/resources/national-championships-calendar',
    icon: CalendarDays,
    eyebrow: 'Road to Nationals',
    title: 'Championship calendar & trip calculator',
    body: 'US youth national championships for 2026–27, plus a calculator that turns the trip into a fundraising goal.',
  },
  {
    to: '/resources/podcasts-and-books-for-sports-parents',
    icon: Headphones,
    eyebrow: 'Listen & read',
    title: 'Best podcasts & books for sports parents',
    body: 'Ten shows, ten books and the free organizations every youth-sports family should know.',
  },
];

const RESOURCES_DESCRIPTION =
  'Guides and insights to help athletes raise support and run a fund page that supporters love.';

export default function Resources() {
  usePageMeta('Resources', RESOURCES_DESCRIPTION);
  const { t } = useTranslation();

  const [feature, ...rest] = articles;

  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="Resources"
          title={
            <>
              Field guides for athletes{' '}
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">
                raising real support.
              </span>
            </>
          }
          lead={t('resourcesPage.subtitle')}
          size="sm"
        />
      }
    >
      <LightSection>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {TOOLKITS.map(({ to, icon: Icon, eyebrow, title, body }) => (
            <Link
              key={to}
              to={to}
              className="group flex flex-col rounded-3xl border border-slate-200/70 bg-slate-50 p-6 hover:bg-white hover:border-slate-300 hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-lg"
            >
              <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-700 grid place-items-center">
                <Icon className="h-5 w-5" aria-hidden />
              </div>
              <div className="mt-5 text-xs uppercase tracking-widest font-semibold text-blue-700">{eyebrow}</div>
              <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900 group-hover:text-blue-700 transition-colors leading-tight">
                {title}
              </h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
              <div className="mt-auto pt-4 inline-flex items-center gap-1.5 text-blue-700 font-semibold text-sm">
                Open <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>

        <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mb-6">Guides & templates</h2>
        {feature && (
          <Link
            to={`/resources/${feature.slug}`}
            className="group block rounded-3xl overflow-hidden bg-[#070b18] text-white ring-1 ring-white/10 relative mb-10"
          >
            <div className="grid lg:grid-cols-2">
              <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                <img
                  src={feature.image}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#070b18]/40 via-transparent to-[#070b18]/40" aria-hidden />
              </div>
              <div className="relative p-8 md:p-12 flex flex-col justify-between">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-yellow-400/15 border border-yellow-400/30 px-3 py-1 text-xs font-semibold text-yellow-300 tracking-wide">
                    Featured
                  </span>
                  <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] group-hover:text-yellow-200 transition-colors">
                    {t(`resourceArticles.${feature.translationKey}.title`)}
                  </h2>
                  <p className="mt-5 text-slate-300 text-lg leading-relaxed">
                    {t(`resourceArticles.${feature.translationKey}.excerpt`)}
                  </p>
                </div>
                <div className="mt-8 inline-flex items-center gap-2 text-yellow-300 font-semibold">
                  {t('resourcesPage.readArticle')}
                  <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </div>
          </Link>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          {rest.map((a) => (
            <Link
              key={a.slug}
              to={`/resources/${a.slug}`}
              className="group block rounded-3xl overflow-hidden border border-slate-200/70 bg-white hover:border-slate-300 hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-xl"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={a.image}
                  alt=""
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold tracking-tight text-slate-900 group-hover:text-blue-700 transition-colors leading-tight">
                  {t(`resourceArticles.${a.translationKey}.title`)}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed text-sm">
                  {t(`resourceArticles.${a.translationKey}.excerpt`)}
                </p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-blue-700 font-semibold text-sm">
                  {t('resourcesPage.readArticle')}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-14 rounded-3xl border border-slate-200 bg-slate-50 p-8 md:flex md:items-center md:justify-between gap-8">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-blue-700">Athlete stories</div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">Funding a season the hard way? Tell us how.</h2>
            <p className="mt-2 text-slate-600 max-w-2xl">
              We interview athletes, parents and coaches about what their sport really costs and how they paid for it. If
              you'd share your story, we'd love to hear it.
            </p>
          </div>
          <Link
            to="/contact"
            className="mt-6 md:mt-0 shrink-0 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 font-semibold transition-colors"
          >
            Share your story <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-10 text-center">
          <a
            href="/rss.xml"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-700 transition-colors"
          >
            Subscribe via RSS
          </a>
        </div>
      </LightSection>

      <ClosingBanner
        eyebrow="Ready to apply it?"
        title={
          <>
            Build your fund page <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">in two minutes.</span>
          </>
        }
        lead="The best resource for fundraising is a fund page that's actually live. Set yours up free."
        primary={
          <Link to="/signup-creator">
            <PrimaryCTA>Start your fund page <ArrowRight className="h-5 w-5" /></PrimaryCTA>
          </Link>
        }
        secondary={
          <Link to="/how-it-works">
            <SecondaryCTA>How it works</SecondaryCTA>
          </Link>
        }
      />
    </PageShell>
  );
}
